import { useState, useEffect, lazy, Suspense, useCallback } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useLocation, useNavigate } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ProfileProvider, useProfile } from './shared/contexts/ProfileContext';

import { DropdownProvider } from './shared/contexts/DropdownContext';
import { SearchProvider } from './shared/contexts/SearchContext';
// REMOVED [Emergency Access]: EmergencyAccessProvider removed — feature deleted
import { LocalModeProvider } from './shared/contexts/LocalModeContext';

import { NetworkStatus } from './shared/components/feedback/NetworkStatus';
import { AutoReminderSync } from './shared/hooks/useAutoReminderSync';
import { ErrorBoundary } from './shared/components/ErrorBoundary';
// Old feedback modal — hidden, not deleted
// import { BetaTestingMode } from './shared/components/ui/BetaTestingMode';
import { FeedbackFab } from './shared/components/ui/FeedbackFab';
import { MaintenancePage } from './shared/components/ui/MaintenancePage';

import { NotificationService } from './shared/services/notificationService';
import { sessionTimeoutService } from './shared/services/sessionTimeoutService';
import { firebaseAuthService } from './shared/services/firebaseAuthService';
import { showToast } from './shared/components/ui/Toast';
import { consumeToasts, clearToasts, queueToast } from './shared/utils/pendingToast';
import { invitationService } from './shared/services/invitationService';
import { EntitySheet } from './design-system/patterns';
import { useSystemConfig } from './shared/hooks/useSystemConfig';
import { resetFirestoreCache } from './shared/config/firebase';

/**
 * Global handler for unhandled promise rejections (especially Firestore cache errors)
 * Silently clears stale Firestore IndexedDB cache without reloading - fixes issue on next page load
 */
function setupGlobalErrorHandlers(): () => void {
  let hasCleared = false;
  const reloadGuardKey = 'lucidDock_firestoreCacheResetAttempted';

  const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
    const error = event.reason;
    const message = error?.message?.toLowerCase() || '';

    // Check if this is a Firestore internal assertion error (cache corruption)
    const isFirestoreCacheError =
      message.includes('firestore') &&
      (message.includes('internal assertion failed') ||
        message.includes('unexpected state') ||
        message.includes('target already exists'));

    if (isFirestoreCacheError && !hasCleared) {
      hasCleared = true;
      event.preventDefault();

      const hasReloaded = sessionStorage.getItem(reloadGuardKey) === 'true';
      if (hasReloaded) {
        console.warn('Firestore cache error detected again; skipping auto-reload to avoid loop');
        return;
      }

      sessionStorage.setItem(reloadGuardKey, 'true');
      console.warn('Firestore cache error detected, resetting cache and reloading');
      resetFirestoreCache()
        .catch(() => {
          // Ignore reset failures - fallback to reload
        })
        .finally(() => {
          window.location.reload();
        });
    }
  };

  window.addEventListener('unhandledrejection', handleUnhandledRejection);

  return () => {
    window.removeEventListener('unhandledrejection', handleUnhandledRejection);
  };
}

// Set up global error handlers immediately (before React renders)
setupGlobalErrorHandlers();

// Helper function to handle chunk loading failures with automatic retry
// This fixes "Failed to fetch dynamically imported module" errors after deployments
function lazyWithRetry<T extends React.ComponentType<React.ComponentProps<T>>>(
  importFn: () => Promise<{ [key: string]: T }>,
  namedExport: string
): React.LazyExoticComponent<T> {
  return lazy(() =>
    importFn()
      .then((module) => ({ default: module[namedExport] }))
      .catch((error: Error) => {
        // Check if it's a chunk loading error
        if (error.message.includes('Failed to fetch dynamically imported module') ||
            error.message.includes('Loading chunk') ||
            error.message.includes('Loading CSS chunk')) {
          // Force reload the page to get fresh chunks
          window.location.reload();
          // Return a never-resolving promise (page will reload before this matters)
          return new Promise<never>(() => {});
        }
        throw error;
      })
  ) as React.LazyExoticComponent<T>;
}

// Lazy load page components for better performance and code splitting
// Using lazyWithRetry wrapper to handle chunk loading failures after deployments
const Address = lazyWithRetry(() => import('./pages/address/AddressPage'), 'Address');
const Personal = lazyWithRetry(() => import('./pages/personal/PersonalPage'), 'Personal');
const FamilySharing = lazyWithRetry(() => import('./pages/FamilySharing'), 'FamilySharing');
const MemberInvite = lazyWithRetry(() => import('./pages/MemberInvite'), 'MemberInvite');
const Auth = lazyWithRetry(() => import('./pages/Auth'), 'Auth');
const AdminMetrics = lazyWithRetry(() => import('./pages/AdminMetrics'), 'AdminMetrics');
const ShareBundlePreview = lazyWithRetry(() => import('./pages/ShareBundlePreview'), 'ShareBundlePreview');
const GoogleDriveCallback = lazyWithRetry(() => import('./pages/GoogleDriveCallback'), 'default');
const PrivacyPolicy = lazyWithRetry(() => import('./pages/PrivacyPolicy'), 'PrivacyPolicy');
const TermsOfService = lazyWithRetry(() => import('./pages/TermsOfService'), 'TermsOfService');

// Empty Suspense fallback — each page renders its own Spinner with a label
function PageLoader() {
  return <div className="min-h-screen" />;
}

// Component to handle toast notifications on route changes.
// Consumes pending toasts queued via queueToast() before navigation.
function ToastHandler() {
  const location = useLocation();

  useEffect(() => {
    // Only show toasts on app pages, not on landing page
    if (location.pathname === '/') {
      clearToasts();
      return;
    }

    const toasts = consumeToasts();
    for (const toast of toasts) {
      const handler = showToast[toast.type as keyof typeof showToast];
      if (typeof handler === 'function') {
        handler(toast.message);
      }
    }
  }, [location]);

  return null;
}

// Component to handle session timeout (1 hour of inactivity)
function SessionTimeoutHandler() {
  const location = useLocation();
  const navigate = useNavigate();

  // Handle session expiration
  const handleSessionExpired = useCallback(async () => {
    // Sign out from Firebase first (clears auth state + localStorage)
    try {
      await firebaseAuthService.signOut();
    } catch {
      // Fallback: manually clear session if Firebase signOut fails
      sessionTimeoutService.expireSession();
    }

    // Show toast notification
    showToast.warning('You have been signed out due to inactivity.');

    // Redirect to landing page
    navigate('/', { replace: true });
  }, [navigate]);

  useEffect(() => {
    // Skip session management on landing page, invite page, share bundle page, emergency access page, and OAuth callback pages
    if (location.pathname === '/' || location.pathname.startsWith('/invite') || location.pathname.startsWith('/share') || location.pathname.startsWith('/oauth')) {
      return;
    }

    // Check if user is authenticated
    const isAuthenticated = localStorage.getItem('isAuthenticated') === 'true';
    if (!isAuthenticated) {
      return;
    }

    // Check if session has already expired on page load/refresh
    if (sessionTimeoutService.isSessionExpired()) {
      handleSessionExpired();
      return;
    }

    // Start activity tracking (updates lastActivity timestamp on user interaction)
    const cleanupActivity = sessionTimeoutService.startActivityTracking();

    // Start periodic session check (checks every minute if session expired)
    const cleanupCheck = sessionTimeoutService.startSessionCheck(handleSessionExpired);

    // Check session immediately when tab becomes visible again (catches suspended intervals)
    const handleVisibilityChange = () => {
      if (document.visibilityState === 'visible' && sessionTimeoutService.isSessionExpired()) {
        handleSessionExpired();
      }
    };
    document.addEventListener('visibilitychange', handleVisibilityChange);

    // Cleanup on unmount
    return () => {
      cleanupActivity();
      cleanupCheck();
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [location.pathname, handleSessionExpired]);

  return null;
}

// Shows a choice dialog when a pending family invitation exists after sign-in/sign-up.
// User chooses "Join Family" or "My Dashboard" — reusable for any invitation flow.
function PendingInvitationHandler() {
  const location = useLocation();
  const { currentProfile, isLoading } = useProfile();
  const [invitation, setInvitation] = useState<{
    invitationId: string;
    profileId: string;
    profileOwnerName: string;
  } | null>(null);
  const [isAccepting, setIsAccepting] = useState(false);

  // Check for pending invitation on app pages (skip landing, invite, share, etc.)
  // Only show when user has an active profile (pilot doc exists in Firestore)
  useEffect(() => {
    // Wait for profile loading to finish before checking
    if (isLoading) return;

    // Must have an active profile — don't show if signup failed or account doesn't exist
    if (!currentProfile) {
      setInvitation(null);
      return;
    }

    const isAppPage = location.pathname !== '/'
      && !location.pathname.startsWith('/invite')
      && !location.pathname.startsWith('/share')
      && !location.pathname.startsWith('/oauth')
      && !location.pathname.startsWith('/privacy')
      && !location.pathname.startsWith('/terms');

    if (!isAppPage) return;

    const raw = sessionStorage.getItem('pendingInvitation');
    if (!raw) return;

    try {
      const data = JSON.parse(raw);
      if (data.invitationId && data.profileId && data.profileOwnerName) {
        setInvitation(data);
      }
    } catch {
      sessionStorage.removeItem('pendingInvitation');
    }
  }, [location.pathname, isLoading, currentProfile]);

  const handleJoinFamily = async () => {
    if (!invitation) return;
    setIsAccepting(true);
    try {
      await invitationService.accept(invitation.invitationId, invitation.profileId);
      sessionStorage.removeItem('pendingInvitation');
      queueToast('success', `You've joined ${invitation.profileOwnerName}'s family!`);

      // Switch to family mode so ProfileContext loads the shared profile (not user's own Owner profile)
      localStorage.setItem('lucidDock_userType', 'family');

      // Full page reload to re-initialize ProfileContext with both profiles
      window.location.href = '/family';
    } catch (err) {
      console.error('Failed to accept invitation:', err);
      showToast.error('Failed to join family. Please try again.');
      setIsAccepting(false);
    }
  };

  const handleMyDashboard = () => {
    sessionStorage.removeItem('pendingInvitation');
    setInvitation(null);
  };

  return (
    <EntitySheet
      variant="dialog"
      isOpen={!!invitation}
      onClose={handleMyDashboard}
      title={`Join ${invitation?.profileOwnerName}'s Family?`}
      message={`You've been invited to join ${invitation?.profileOwnerName}'s family on LucidDock. You can join now or continue to your own dashboard.`}
      confirmLabel="Join Family"
      confirmVariant="primary"
      onConfirm={handleJoinFamily}
      cancelLabel="My LucidDock"
      loading={isAccepting}
    />
  );
}

function App() {
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(true);
  const { maintenanceMode, maintenanceMessage } = useSystemConfig();

  // Initialize sidebar state once at app level
  useEffect(() => {
    const savedState = localStorage.getItem('sidebarCollapsed');

    if (savedState !== null) {
      // Use saved preference
      setIsSidebarCollapsed(JSON.parse(savedState));
    } else {
      // Default: collapsed on mobile, expanded on desktop
      const isDesktop = window.innerWidth >= 1024;
      setIsSidebarCollapsed(!isDesktop);
    }

    const handleResize = () => {
      // Only auto-adjust if no saved preference exists
      if (savedState === null) {
        if (window.innerWidth >= 1024) {
          setIsSidebarCollapsed(false);
        } else {
          setIsSidebarCollapsed(true);
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Initialize notification permissions and Google Drive save failure listener
  useEffect(() => {
    // Request notification permission on app load
    NotificationService.requestNotificationPermission();

    // Listen for Google Drive save failures to show error toast
    const handleSaveFailed = (event: Event) => {
      const customEvent = event as CustomEvent<{ message: string }>;
      showToast.error(customEvent.detail?.message || 'Failed to save data. Please try again.');
    };
    window.addEventListener('luciddock:save-failed', handleSaveFailed);

    return () => {
      window.removeEventListener('luciddock:save-failed', handleSaveFailed);
    };
  }, []);

  const handleSidebarToggle = (collapsed: boolean) => {
    setIsSidebarCollapsed(collapsed);
    // Save user preference
    localStorage.setItem('sidebarCollapsed', JSON.stringify(collapsed));
  };

  const pageProps = {
    isSidebarCollapsed,
    onSidebarToggle: handleSidebarToggle,
  };

  // If maintenance mode is enabled, show maintenance page (except for admin routes)
  const pathname = window.location.pathname;
  const isAdminRoute = pathname.startsWith('/admin');

  if (maintenanceMode && !isAdminRoute) {
    return (
      <MaintenancePage message={maintenanceMessage} />
    );
  }

  return (
    <GoogleOAuthProvider clientId={import.meta.env.VITE_GOOGLE_CLIENT_ID || ''}>
      <BrowserRouter>
          <LocalModeProvider>
              <ProfileProvider>
                <SearchProvider>
                  <DropdownProvider>
                    <ToastHandler />
                    <SessionTimeoutHandler />
                    <PendingInvitationHandler />
                    <AutoReminderSync />
                    <NetworkStatus />
                    <FeedbackFab />
                    <ErrorBoundary>
                      <Suspense fallback={<PageLoader />}>
                        <Routes>
                        <Route path="/" element={<Auth />} />
                        <Route path="/home" element={<Navigate to="/address" replace />} />
                        <Route path="/dashboard" element={<Navigate to="/address" replace />} />
                        <Route path="/address" element={<Address {...pageProps} />} />
                        <Route path="/personal" element={<Personal {...pageProps} />} />
                        {/* Hidden pages — redirect to /address (feature simplification) */}
                        <Route path="/vehicles" element={<Navigate to="/address" replace />} />
                        <Route path="/healthcare" element={<Navigate to="/address" replace />} />
                        <Route path="/finance" element={<Navigate to="/address" replace />} />
                        <Route path="/insurance" element={<Navigate to="/address" replace />} />
                        {/* REMOVED [Business Mode]: /clients route — app is personal/family only */}
                        <Route path="/clients" element={<Navigate to="/address" replace />} />
                        <Route path="/family" element={<FamilySharing {...pageProps} />} />
                        <Route path="/invite/:invitationId" element={<MemberInvite />} />
                        <Route path="/share/:bundleId" element={<ShareBundlePreview />} />
                        {/* REMOVED [Emergency Access]: /emergency-access route removed — feature deleted */}
                        <Route path="/oauth/google-drive" element={<GoogleDriveCallback />} />
                        <Route path="/auth" element={<Navigate to="/" replace />} />
                        <Route path="/admin/settings" element={<AdminMetrics {...pageProps} />} />
                        <Route path="/admin/dashboard" element={<Navigate to="/admin/settings" replace />} />
                        <Route path="/admin/metrics" element={<Navigate to="/admin/settings" replace />} />
                        <Route path="/privacy" element={<PrivacyPolicy />} />
                        <Route path="/terms" element={<TermsOfService />} />
                        <Route path="*" element={<Navigate to="/" replace />} />
                      </Routes>
                      </Suspense>
                    </ErrorBoundary>
                  </DropdownProvider>
                </SearchProvider>
            </ProfileProvider>
          </LocalModeProvider>
      </BrowserRouter>
    </GoogleOAuthProvider>
  );
}

export default App;
